import {
  collection,
  doc,
  getDocFromServer,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { isValidCivilDate } from '../domain/finance';

export const FINANCIAL_OCCURRENCE_PAGE_SIZE = 100;
const MAX_FINANCIAL_OCCURRENCE_PAGES = 1000;

export const FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES = Object.freeze({
  ACCOUNT_REQUIRED: 'FINANCIAL_OCCURRENCE_ACCOUNT_REQUIRED',
  INVALID_RANGE: 'INVALID_FINANCIAL_OCCURRENCE_QUERY_RANGE',
  PAGE_LIMIT_EXCEEDED: 'FINANCIAL_OCCURRENCE_PAGE_LIMIT_EXCEEDED',
});

const assertQueryInput = ({ accountId, startDate, endDate }) => {
  if (typeof accountId !== 'string' || !accountId) {
    throw Object.assign(new Error(FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.ACCOUNT_REQUIRED), {
      code: FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.ACCOUNT_REQUIRED,
    });
  }
  if (!isValidCivilDate(startDate) || !isValidCivilDate(endDate) || startDate > endDate) {
    throw Object.assign(new Error(FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.INVALID_RANGE), {
      code: FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.INVALID_RANGE,
    });
  }
};

const readAllPages = async ({ source, constraints }) => {
  const documents = [];
  let cursor = null;
  let pageCount = 0;
  while (true) {
    if (pageCount >= MAX_FINANCIAL_OCCURRENCE_PAGES) {
      throw Object.assign(new Error(FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.PAGE_LIMIT_EXCEEDED), {
        code: FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.PAGE_LIMIT_EXCEEDED,
      });
    }
    const snapshot = await getDocsFromServer(query(
      source,
      ...constraints,
      ...(cursor ? [startAfter(cursor)] : []),
      limit(FINANCIAL_OCCURRENCE_PAGE_SIZE)
    ));
    documents.push(...snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    pageCount += 1;
    if (snapshot.size < FINANCIAL_OCCURRENCE_PAGE_SIZE) break;
    cursor = snapshot.docs.at(-1);
    if (!cursor) {
      throw Object.assign(new Error(FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.PAGE_LIMIT_EXCEEDED), {
        code: FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.PAGE_LIMIT_EXCEEDED,
      });
    }
  }
  return documents;
};

export const loadTransactionsForOccurrenceRange = async ({
  accountId, startDate, endDate,
}, firestoreDb = firestore) => {
  assertQueryInput({ accountId, startDate, endDate });
  return readAllPages({
    source: collection(firestoreDb, 'accounts', accountId, 'transactions'),
    constraints: [
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc'),
    ],
  });
};

export const loadRecurringSegmentsForOccurrenceRange = async ({
  accountId, startDate, endDate,
}, firestoreDb = firestore) => {
  assertQueryInput({ accountId, startDate, endDate });
  const candidates = await readAllPages({
    source: collection(firestoreDb, 'accounts', accountId, 'recurringTransactions'),
    constraints: [
      where('effectiveFromDate', '<=', endDate),
      orderBy('effectiveFromDate', 'asc'),
    ],
  });
  return candidates.filter((segment) => (
    (!segment.effectiveUntilDate || segment.effectiveUntilDate >= startDate)
    && (!segment.schedule?.untilDate || segment.schedule.untilDate >= startDate)
  ));
};

export const loadOccurrenceStatesForRange = async ({
  accountId, startDate, endDate,
}, firestoreDb = firestore) => {
  assertQueryInput({ accountId, startDate, endDate });
  const source = collection(firestoreDb, 'accounts', accountId, 'recurringOccurrenceStates');
  const [scheduledStates, effectiveStates] = await Promise.all([
    readAllPages({
      source,
      constraints: [
        where('scheduledDate', '>=', startDate),
        where('scheduledDate', '<=', endDate),
        orderBy('scheduledDate', 'asc'),
      ],
    }),
    readAllPages({
      source,
      constraints: [
        where('effectiveDate', '>=', startDate),
        where('effectiveDate', '<=', endDate),
        orderBy('effectiveDate', 'asc'),
      ],
    }),
  ]);
  return Array.from(new Map(
    [...scheduledStates, ...effectiveStates].map((state) => [state.id, state])
  ).values());
};

const hydrateLinkedDocuments = async ({
  firestoreDb,
  accountId,
  collectionName,
  ids,
  loaded,
}) => {
  const loadedIds = new Set(loaded.map(({ id }) => id));
  const missingIds = [...new Set(ids)].filter((id) => id && !loadedIds.has(id));
  if (missingIds.length === 0) return loaded;
  const snapshots = await Promise.all(missingIds.map((id) => getDocFromServer(
    doc(firestoreDb, 'accounts', accountId, collectionName, id)
  )));
  return [
    ...loaded,
    ...snapshots.filter((snapshot) => snapshot.exists()).map((snapshot) => ({
      id: snapshot.id,
      ...snapshot.data(),
    })),
  ];
};

export const loadFinancialOccurrenceSources = async (range, firestoreDb = firestore) => {
  assertQueryInput(range);
  const [rangeTransactions, rangeSegments, occurrenceStates] = await Promise.all([
    loadTransactionsForOccurrenceRange(range, firestoreDb),
    loadRecurringSegmentsForOccurrenceRange(range, firestoreDb),
    loadOccurrenceStatesForRange(range, firestoreDb),
  ]);
  const [transactions, recurringSegments] = await Promise.all([
    hydrateLinkedDocuments({
      firestoreDb,
      accountId: range.accountId,
      collectionName: 'transactions',
      ids: occurrenceStates.map(({ transactionId }) => transactionId),
      loaded: rangeTransactions,
    }),
    hydrateLinkedDocuments({
      firestoreDb,
      accountId: range.accountId,
      collectionName: 'recurringTransactions',
      ids: occurrenceStates.map(({ segmentId }) => segmentId),
      loaded: rangeSegments,
    }),
  ]);
  return { transactions, recurringSegments, occurrenceStates };
};

